/**
 * Admin session is a localStorage wrapper. The current rollout accepts both
 * real Cognito tokens and the legacy self-issued `admin:<name>:<deviceId>`
 * token so we can deploy auth without locking out the existing dashboard.
 */

import type { AppRole, DashboardSessionProfile } from '@cer-autofill/shared';

const TOKEN_KEY = 'cer-admin-device-token';
const NAME_KEY = 'cer-admin-name';
const DEVICE_KEY = 'cer-admin-device-id';
const ID_TOKEN_KEY = 'cer-admin-id-token';
const ACCESS_TOKEN_KEY = 'cer-admin-access-token';
const REFRESH_TOKEN_KEY = 'cer-admin-refresh-token';
const EXPIRES_AT_KEY = 'cer-admin-expires-at';
const EMAIL_KEY = 'cer-admin-email';
const USER_ID_KEY = 'cer-admin-user-id';
const ROLES_KEY = 'cer-admin-roles';
const INSPECTOR_ID_KEY = 'cer-admin-inspector-id';
const DROPBOX_LEGACY_IMPORT_KEY = 'cer-admin-dropbox-legacy-import';

export interface AdminSession {
  readonly name: string;
  readonly deviceId: string;
  readonly deviceToken: string;
  readonly email?: string;
  readonly userId?: string;
  readonly roles: AppRole[];
  readonly inspectorId?: string;
  /**
   * Reports-page "Sync Dropbox" switch from the session profile.
   * Absent until a profile is stored; only an explicit false hides the button.
   */
  readonly dropboxLegacyImportEnabled?: boolean;
  readonly idToken?: string;
  readonly accessToken?: string;
  readonly refreshToken?: string;
  readonly expiresAt?: number;
  readonly source: 'cognito' | 'legacy';
}

/** Stable per-install identifier. Generated once and persisted. */
export function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

export function getSession(): AdminSession | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const name = localStorage.getItem(NAME_KEY);
  const deviceId = localStorage.getItem(DEVICE_KEY);
  if (!token || !name || !deviceId) return null;
  if (!token.startsWith('admin:')) return null;
  const idToken = localStorage.getItem(ID_TOKEN_KEY) ?? undefined;
  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY) ?? undefined;
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY) ?? undefined;
  const email = localStorage.getItem(EMAIL_KEY) ?? undefined;
  const expiresAtRaw = localStorage.getItem(EXPIRES_AT_KEY);
  const expiresAt = expiresAtRaw ? Number.parseInt(expiresAtRaw, 10) : undefined;
  if (idToken && expiresAt && Date.now() > expiresAt && !refreshToken) {
    clearSession();
    return null;
  }
  const roles = parseRoles(localStorage.getItem(ROLES_KEY));
  const userId = localStorage.getItem(USER_ID_KEY) ?? undefined;
  const inspectorId = localStorage.getItem(INSPECTOR_ID_KEY) ?? undefined;
  const dropboxLegacyImport = localStorage.getItem(DROPBOX_LEGACY_IMPORT_KEY);
  const dropboxLegacyImportEnabled =
    dropboxLegacyImport === 'true' ? true : dropboxLegacyImport === 'false' ? false : undefined;
  return {
    deviceToken: token,
    name,
    deviceId,
    source: idToken ? 'cognito' : 'legacy',
    roles: idToken ? roles : ['admin'],
    ...(email !== undefined ? { email } : {}),
    ...(userId !== undefined ? { userId } : {}),
    ...(inspectorId !== undefined ? { inspectorId } : {}),
    ...(dropboxLegacyImportEnabled !== undefined ? { dropboxLegacyImportEnabled } : {}),
    ...(idToken !== undefined ? { idToken } : {}),
    ...(accessToken !== undefined ? { accessToken } : {}),
    ...(refreshToken !== undefined ? { refreshToken } : {}),
    ...(expiresAt !== undefined ? { expiresAt } : {}),
  };
}

export function setSession(name: string): AdminSession {
  const deviceId = getOrCreateDeviceId();
  const token = `admin:${name}:${deviceId}`;
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(NAME_KEY, name);
  clearCognitoTokens();
  return { name, deviceId, deviceToken: token, roles: ['admin'], source: 'legacy' };
}

export function setCognitoSession(input: {
  name: string;
  email: string;
  idToken: string;
  accessToken?: string;
  refreshToken?: string;
  expiresInSeconds?: number;
}): AdminSession {
  const deviceId = getOrCreateDeviceId();
  const token = `admin:${input.name}:${deviceId}`;
  const expiresAt = Date.now() + (input.expiresInSeconds ?? 3600) * 1000;
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(NAME_KEY, input.name);
  localStorage.setItem(EMAIL_KEY, input.email);
  localStorage.setItem(ID_TOKEN_KEY, input.idToken);
  if (input.accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, input.accessToken);
  if (input.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, input.refreshToken);
  localStorage.setItem(EXPIRES_AT_KEY, String(expiresAt));
  return {
    name: input.name,
    email: input.email,
    deviceId,
    deviceToken: token,
    roles: [],
    idToken: input.idToken,
    ...(input.accessToken !== undefined ? { accessToken: input.accessToken } : {}),
    ...(input.refreshToken !== undefined ? { refreshToken: input.refreshToken } : {}),
    expiresAt,
    source: 'cognito',
  };
}

export function setDashboardProfile(profile: DashboardSessionProfile): AdminSession {
  localStorage.setItem(NAME_KEY, profile.displayName);
  localStorage.setItem(EMAIL_KEY, profile.email);
  localStorage.setItem(USER_ID_KEY, profile.userId);
  localStorage.setItem(ROLES_KEY, JSON.stringify(profile.roles));
  if (profile.inspectorId) localStorage.setItem(INSPECTOR_ID_KEY, profile.inspectorId);
  else localStorage.removeItem(INSPECTOR_ID_KEY);
  localStorage.setItem(
    DROPBOX_LEGACY_IMPORT_KEY,
    profile.dropboxLegacyImportEnabled ? 'true' : 'false',
  );
  const session = getSession();
  if (!session) throw new Error('Cannot attach a profile without an active session.');
  return session;
}

export function updateCognitoTokens(input: {
  idToken: string;
  accessToken?: string;
  expiresInSeconds?: number;
}): AdminSession {
  const current = getSession();
  if (!current || current.source !== 'cognito') {
    throw new Error('Cannot refresh a non-Cognito session.');
  }
  localStorage.setItem(ID_TOKEN_KEY, input.idToken);
  if (input.accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, input.accessToken);
  localStorage.setItem(
    EXPIRES_AT_KEY,
    String(Date.now() + (input.expiresInSeconds ?? 3600) * 1000),
  );
  const refreshed = getSession();
  if (!refreshed) throw new Error('Refreshed session could not be loaded.');
  return refreshed;
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(NAME_KEY);
  clearCognitoTokens();
  // We keep deviceId across logouts so it stays stable for the device.
}

function clearCognitoTokens(): void {
  localStorage.removeItem(ID_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(EXPIRES_AT_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(ROLES_KEY);
  localStorage.removeItem(INSPECTOR_ID_KEY);
  localStorage.removeItem(DROPBOX_LEGACY_IMPORT_KEY);
}

function parseRoles(value: string | null): AppRole[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (role): role is AppRole =>
        typeof role === 'string' && ['inspector', 'admin', 'qa', 'support'].includes(role),
    );
  } catch {
    return [];
  }
}
